import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid2,
  Typography,
  Box,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckInOutDetail from './CheckInOutDetail'
import { CheckInOutRecord } from '../../types/checkinout'


interface CheckInOutDetailDialogProps {
  open: boolean
  onClose: () => void
  selectedRecord: CheckInOutRecord | null
  onImageClick: (image: string, isCheckIn: boolean) => void
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const CheckInOutDetailDialog: React.FC<CheckInOutDetailDialogProps> = ({
  open,
  onClose,
  selectedRecord,
  onImageClick,
}) => {
  if (!selectedRecord) return null

  const project = selectedRecord.checkin.project

  const isDistanceExceeded = () => {
    const distance = selectedRecord.checkin.distanceInKm || 0
    const isExceeded = distance > (project?.radius || 0)
    return isExceeded
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        รายละเอียดการเข้า-ออกงาน
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid2 container spacing={3}>
          <Grid2 container direction="row" size={{ xs: 12, md: 12 }}>
            <Grid2 size={{ xs: 12, sm: 2 }} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 300 }}>
                ข้อมูลพนักงาน
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedRecord.checkin?.user?.[0]?.name ||
                  selectedRecord.username}
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 2.5 }} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 300 }}>
                รหัสพนักงาน
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedRecord.checkin?.user?.[0]?.employeeCode || '-'}
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 3 }} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 300 }}>
                ชื่อสถานที่
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedRecord.checkin?.project?.name || '-'}
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 2 }} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 300 }}>
                วันที่
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(selectedRecord.checkin.activeTime)}
              </Typography>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 2 }} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 300 }}>
                พิกัดละติจูด และลองจิจูด
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedRecord.checkin?.project?.latitude},{' '}
                {selectedRecord.checkin?.project?.longitude}
              </Typography>
            </Grid2>
          </Grid2>

          <Grid2 container direction="row" size={{ xs: 12, md: 12 }}>
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ mb: 2 }}>
              {selectedRecord.checkin && (
                <CheckInOutDetail
                  type="checkin"
                  activeTime={selectedRecord.checkin.activeTime}
                  location={selectedRecord.checkin.location}
                  note={selectedRecord.checkin.note}
                  images={selectedRecord.checkin.images}
                  isDistanceExceeded={selectedRecord.checkin.isDistanceExceeded}
                  onImageClick={(image) => onImageClick(image, true)}
                />
              )}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ mb: 2 }}>
              {selectedRecord.checkout &&
                selectedRecord.checkout.map((checkout, index) => (
                  <Box key={`checkout-${index}`}>
                    <CheckInOutDetail
                      type="checkout"
                      label={`ออกงานครั้งที่ ${index + 1}`}
                      activeTime={checkout.activeTime}
                      location={checkout.location}
                      note={checkout.note}
                      images={checkout.images}
                      isDistanceExceeded={checkout.isDistanceExceeded}
                      onImageClick={(image) => onImageClick(image, false)}
                    />
                  </Box>
                ))}
            </Grid2>
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', mb: 2, mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
          onClick={onClose}
        >
          ปิดหน้าต่าง
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CheckInOutDetailDialog
